// Real 3D thumbnails for prefab props: each prefab's parts are rendered once
// to a small offscreen WebGL canvas and cached as a data URL. Because they're
// generated from the live parts list, thumbnails always match the object.
import * as THREE from 'three';
import { Prefab } from '../prefabs';

const SIZE = 160;
const cache = new Map<string, string>();
let renderer: THREE.WebGLRenderer | null = null;

export function prefabThumb(prefab: Prefab): string {
  const hit = cache.get(prefab.id);
  if (hit) return hit;

  try {
    renderer ??= new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
    renderer.setSize(SIZE, SIZE);

    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 0.85));
    const key = new THREE.DirectionalLight(0xffffff, 1.8);
    key.position.set(2, 3, 4);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x3fecdb, 0.7);
    rim.position.set(-3, 2, -2);
    scene.add(rim);

    const bounds = new THREE.Box3();
    for (const part of prefab.parts) {
      let geo: THREE.BufferGeometry;
      switch (part.shape) {
        case 'cylinder': geo = new THREE.CylinderGeometry(0.5, 0.5, 1, 24); break;
        case 'sphere': geo = new THREE.SphereGeometry(0.5, 24, 16); break;
        case 'cone': geo = new THREE.ConeGeometry(0.5, 1, 24); break;
        case 'torus': geo = new THREE.TorusGeometry(0.4, 0.15, 12, 24); break;
        default: geo = new THREE.BoxGeometry(1, 1, 1);
      }
      const mat = new THREE.MeshStandardMaterial({
        color: part.color,
        metalness: part.metalness ?? 0,
        roughness: part.roughness ?? 0.5,
        transparent: (part.opacity ?? 1) < 1,
        opacity: part.opacity ?? 1,
      });
      if (part.emissive) {
        mat.emissive = new THREE.Color(part.color);
        mat.emissiveIntensity = part.emissive;
      }
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(...part.position);
      if (part.rotation) mesh.rotation.set(...part.rotation);
      mesh.scale.set(...part.scale);
      mesh.updateMatrixWorld();
      scene.add(mesh);
      bounds.expandByObject(mesh);
    }

    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z, 0.001);

    // 3/4 front view — prop fronts face +Z, so look from front-right-above
    const cam = new THREE.PerspectiveCamera(35, 1, 0.01, 200);
    cam.position.copy(center).addScaledVector(new THREE.Vector3(1, 0.7, 1.35).normalize(), maxDim * 1.9);
    cam.lookAt(center);

    renderer.render(scene, cam);
    const url = renderer.domElement.toDataURL('image/png');

    scene.traverse((o: any) => {
      o.geometry?.dispose?.();
      o.material?.dispose?.();
    });

    cache.set(prefab.id, url);
    return url;
  } catch {
    return ''; // caller falls back to the emoji icon
  }
}
